import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { PROSPECTS_LABEL } from '@shared';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { MojoPrivilegesService } from 'mojo-privileges';
import { Observable, combineLatest, Subscription } from 'rxjs';
import {
  filter,
  switchMap,
  tap,
  map,
  shareReplay,
  first,
  distinctUntilChanged,
} from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { getInnerRouterPath } from '@app/app.reducer';
import { getJobsSelected } from '@app/collections/jobs';
import {
  isCandidateDirectAndSubmitHiringManagerActionIsAvailable,
  loadCandidateHiringManagerStatus,
} from '@collections/hiring-manager-status';
import { AssignConfig, AssignPrivileges } from '@modules/assigns/assign.abstract.config';
import { assignCandidatesLoad, loadTotalElements } from '@modules/assigns/store/assigns.actions';
import {
  getAssignsCandidatesNotLoaded,
  getAssignsCandidatesState,
  getAssignsWithCandidates,
  getAssociationStatusConfigurationsShortList,
} from '@modules/assigns/store/assigns.selectors';
import { JobsConfig } from '@modules/jobs-page/jobs.abstract.config';
import {
  ALL_SELECTED,
  AssociationStatusUtils,
  CandidateWithAssociation,
  Job,
} from '@shared/models';
import { Pagination } from '@shared/pagination';
import { RouteParamsService } from '@shared/services/route-params.service';
import { isEqual } from 'lodash';
import { MojoBatchService } from 'mojo-batch';
import { checkIfBatchRejectForShortlistedIsAvailable } from '@collections/associations';
import { IsMobileViewService } from '@shared/services/is-mobile-view.service';
import { getCountCandidates } from '@app/collections/candidates';
import { HMStatuses } from '@modules/assigns/components/job-assign-collection-header/job-assign-collection-header.component';

@Component({
  selector: 'mojo-assigns-jobs-container',
  templateUrl: './job-assigns.container.html',
  styleUrls: ['./job-assigns.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobAssignsContainerComponent implements OnInit, OnDestroy {
  @Input() expandedView = true;
  @Input() showContainerBorder = false;
  @Input() isWidgetView = false;
  @Output() showlineOutput = new EventEmitter<boolean>();
  list: CandidateWithAssociation[];
  totalCandidates$ = this.store.select(getCountCandidates);
  isExpended = true;
  private sub: Subscription = new Subscription();
  isPanelOpen = true;
  emptyMessage = 'No shortlisted candidates';
  defaultPageSize = 10;
  currentRoute$ = this.store.select(getInnerRouterPath);
  isHiringManager$: Observable<boolean> = this.privilegeService.hasPrivilege(
    this.jobsConfig.privileges.jobs.hiringManager,
  );

  isLoading$ = this.store.select(getAssignsCandidatesNotLoaded);
  isProspectTab = false;
  isShorlistPage = false;
  list$: Observable<CandidateWithAssociation[]> = this.store.select(getAssignsWithCandidates);
  hiringManagerFlow$: Observable<boolean> = this.featureExtensionService.hasFeatureExtension(
    AppFeatureExtensions.HIRING_MANAGER_FLOW,
  );
  isHiringManager: Observable<boolean>;

  job$: Observable<Job> = this.store.select(getJobsSelected);

  statuses$: Observable<string[]> = this.job$.pipe(
    switchMap((job) =>
      this.store.select(getAssociationStatusConfigurationsShortList, { jobId: job.id }).pipe(
        map((configurations) => configurations.map((configuration) => configuration.label)),
        map((statuses) => [ALL_SELECTED, ...statuses]),
      ),
    ),
  );

  isFullPageView$ = this.store.select(getInnerRouterPath).pipe(
    map((route) => ['shortlist'].includes(route)),
    shareReplay(1),
  );

  statusesConfiguration$ = this.store.select(getJobsSelected).pipe(
    switchMap((job) =>
      this.store.select(getAssociationStatusConfigurationsShortList, { jobId: job.id }),
    ),
    filter((statuses) => !!statuses && statuses.length > 0),
    first(),
    tap(() => this.pageChange(0)),
  );

  pagination$: Observable<Pagination> = this.store.select(getAssignsCandidatesState);
  privileges: AssignPrivileges = this.assignConfig.privileges;

  /**
   * TODO: requires refactor to use actually full list of candidates
   * ids not only currrent page
   */
  allAvailableDirectApplicantsIds$ = this.list$.pipe(
    filter((v) => !!v),
    map((associationObj) => {
      const candidateIds = [];
      associationObj.forEach((item) => {
        candidateIds.push(item.candidate.id);
      });
      return candidateIds;
    }),
  );

  isSubmitToHiringManagerAvailable$ = combineLatest([
    this.batchSelectionManagerService.selection$,
    this.job$,
  ]).pipe(
    switchMap(([candidateIds, job]) =>
      this.store.select(
        isCandidateDirectAndSubmitHiringManagerActionIsAvailable({
          jobId: job.id,
          candidateIds,
        }),
      ),
    ),
  );

  isRejectingAvailable$ = combineLatest([
    this.batchSelectionManagerService.selection$,
    this.job$,
  ]).pipe(
    switchMap(([candidateIds, job]) =>
      this.store.select(checkIfBatchRejectForShortlistedIsAvailable(candidateIds, job.id)),
    ),
  );

  constructor(
    private store: Store<{}>,
    private assignConfig: AssignConfig,
    private privilegeService: MojoPrivilegesService,
    private jobsConfig: JobsConfig,
    private featureExtensionService: MojoFeatureExtensionsService,
    private route: ActivatedRoute,
    private routeParamsService: RouteParamsService,
    private readonly privilegesService: MojoPrivilegesService,
    private batchSelectionManagerService: MojoBatchService,
    public isMobileViewService: IsMobileViewService,
  ) {
    this.isHiringManager$ = this.privilegesService.hasPrivilege(this.privileges.hiringManager);
    this.sub.add(
      this.hiringManagerFlow$.subscribe((enabled) => {
        if (enabled) {
          this.fetchHiringManagerStatusForCandidates();
        }
      }),
    );
  }
  ngOnInit(): void {
    this.store.dispatch(
      loadTotalElements({
        params: { offset: 0 },
        filters: [],
      }),
    );

    this.currentRoute$.subscribe((currentPage) => {
      this.isShorlistPage = currentPage === 'shortlist';
    });

    this.list$.subscribe((items) => {
      this.list = items;

      if (this.list.length) {
        this.showlineOutput.emit(true);
      } else {
        this.showlineOutput.emit(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  pageChange(offset: number = 0) {
    this.store.dispatch(
      assignCandidatesLoad({
        params: { offset },
        filters:
          !!this.route.snapshot.queryParams.shortlistedFilters &&
          JSON.parse(this.route.snapshot.queryParams.shortlistedFilters),
        hmSelectedStatuses: this.isProspectTab
          ? []
          : !!this.route.snapshot.queryParams.hmStatus &&
            JSON.parse(this.route.snapshot.queryParams.hmStatus),
      }),
    );
  }

  statusesChange(selectedStatuses: HMStatuses) {
    this.isProspectTab = selectedStatuses.statuses.includes(PROSPECTS_LABEL);
    selectedStatuses.statuses = selectedStatuses.statuses.map((status) =>
      AssociationStatusUtils.extractQuantityFromString(status),
    );

    const queryParamsObj = {
      shortlistedFilters: selectedStatuses.statuses,
      hmStatus: selectedStatuses.hmStatus,
    };
    this.routeParamsService.updateRouteParams(queryParamsObj, this.route);
    this.batchSelectionManagerService.clearSelection();
    this.store.dispatch(
      assignCandidatesLoad({
        params: { offset: 0 },
        filters: selectedStatuses.statuses,
        selectedStatuses: this.isProspectTab ? [] : selectedStatuses.statuses,
        hmSelectedStatuses: this.isProspectTab ? [] : selectedStatuses.hmStatus,
      }),
    );
  }

  private fetchHiringManagerStatusForCandidates(): void {
    this.sub.add(
      combineLatest([this.list$, this.job$])
        .pipe(
          filter(([list, job]) => !!list && !!job),
          map(([list, job]) => [list.map((item) => item.candidate.id), job.id]),
          distinctUntilChanged(isEqual),
        )
        .subscribe(([candidatesIds, jobId]: [number[], number]) => {
          for (const candId of candidatesIds) {
            if (typeof candId !== 'number') {
              return;
            }
          }
          this.store.dispatch(loadCandidateHiringManagerStatus({ jobId, candidatesIds }));
        }),
    );
  }

  expandedData() {
    if (!this.isShorlistPage) this.isPanelOpen = !this.isPanelOpen;
  }
}
